/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect } from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useLocation } from "react-router";
import Typography from "@mui/material/Typography";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { useForm } from "@/src/provider/Form/FormProvider";
import { getCaseLink } from "@/src/services/productService";
import ProductBox from "@/src/components/ProductBox/ProductBox";
import InsuranceData from "@/src/components/Form/Settlement/Summary/InsuranceData";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import EmailVerificationModal from "@/src/components/FormInfoModal/EmailVerificationModal";
import summaryStyle from "@/src/components/Form/ContractLaw/Summary/summaryStyle";
import SummaryDetailsItem from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsItem";
import SummaryDetailsPersonItems from "@/src/components/Form/Common/SummaryDetailsItem/SummaryDetailsPersonItems";
import ContractualPartnerData from "@/src/components/Form/ContractLaw/Summary/ContractualPartnerData";
import { isFlexServiceProduct } from "@/src/services/advocardService";

type SummaryProps = {
  product: AbstractCase;
  submitProps: FormSubmitProps;
};

const Summary = ({ product, submitProps }: SummaryProps) => {
  const location = useLocation();
  const stateField = location.state?.field;
  const { handleSubmit } = useForm();

  useEffect(() => {
    const el = document.getElementById(stateField);
    el && el.scrollIntoView();
  }, [stateField]);

  return (
    <ProductBox>
      <Typography variant={"h5"}>
        Ihre Angaben
      </Typography>
      <Grid container>
        <Box id={"generalCivilLawBase"} sx={summaryStyle.scrollToAnchor} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Ihr Anliegen</Typography>
        </Grid>
        <SummaryDetailsPersonItems basePath={"customer.person"} caseData={product} />

        <SummaryDetailsItem fieldPath={"customer.telephone"} caseData={product} />

        {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && !product.clientWasMinorAtContractDate && (
          <>
            <SummaryDetailsPersonItems basePath={"client"} caseData={product} />
          </>
        )}
        {product.caseCategory !== "advisory" && product.clientIsCustomer !== true && product.clientWasMinorAtContractDate && (
          <>
            <Grid sx={{ marginTop: "2rem", borderBottom: "1px solid #333", "& h6": { lineHeight: "1rem" } }} size={12}>
              <Typography variant={"h6"}>Angaben minderjährige Person</Typography>
            </Grid>

            <SummaryDetailsPersonItems basePath={"client"} caseData={product} />

            <SummaryDetailsPersonItems basePath={"clientLegalRepresentative"} caseData={product} />

            {product.clientLegalRepresentativeAmount === 2 && (
              <>
                <SummaryDetailsPersonItems basePath={"secondClientLegalRepresentative"} caseData={product} />
                <SummaryDetailsItem fieldPath={"clientLegalRepresentativesAuthorizationType"} caseData={product} />
              </>
            )}
          </>
        )}

        {product.caseCategory !== "advisory" && (
          <>
            <Box id={"generalCivilLawIncident"} sx={summaryStyle.scrollToAnchor} />
            <Grid sx={summaryStyle.subHeadline} size={12}>
              <Typography variant={"h6"}>Informationen zum Anliegen</Typography>
            </Grid>
            <SummaryDetailsItem fieldPath={"objectivesClient"} caseData={product} fieldType={"multipleOptions"} />
            <SummaryDetailsItem fieldPath={"clientHasAlreadyClaimed"} caseData={product} />

            {product.clientHasAlreadyClaimed === true && (
              <>
                <SummaryDetailsItem fieldPath={"claimedDateClient"} caseData={product} fieldType={"date"} />
              </>
            )}

            <SummaryDetailsItem fieldPath={"isDamageQuantifiable"} caseData={product} />

            {product.isDamageQuantifiable === true && (
              <>
                <SummaryDetailsItem fieldPath={"claimedAmountClient"} caseData={product} fieldType={"money"} />
                <SummaryDetailsItem fieldPath={"client.bankData.iban"} caseData={product} />
              </>
            )}

            <SummaryDetailsItem fieldPath={"incidentDate"} caseData={product} fieldType={"date"} />
            {product.objectivesClient.indexOf("injunction") > -1 && <SummaryDetailsItem fieldPath={"incidentFrequency"} caseData={product} />}
            <SummaryDetailsItem fieldPath={"incidentLocalityDescription"} caseData={product} />
            <SummaryDetailsItem fieldPath={"incidentAddress.streetAddress"} caseData={product} />
            <SummaryDetailsItem fieldPath={"incidentAddress.postalCode"} caseData={product} />
            <SummaryDetailsItem fieldPath={"incidentAddress.addressLocality"} caseData={product} />
            <SummaryDetailsItem fieldPath={"policeInvolved"} caseData={product} fieldType={"yesNo"} />
            {product.policeInvolved === true && (
              <>
                <SummaryDetailsItem fieldPath={"policeReportReferenceNumber"} caseData={product} />
                <SummaryDetailsItem fieldPath={"policeDepartmentName"} caseData={product} />
              </>
            )}
          </>
        )}

        {product.caseCategory !== "advisory" && (
          <>
            <Box id={"contractualPartnerData"} sx={summaryStyle.scrollToAnchor} />
            <ContractualPartnerData title={"Informationen zur Gegenseite"} formData={product} />
          </>
        )}

        <Box id={"concernSummary"} sx={summaryStyle.scrollToAnchor} />
        <Grid sx={summaryStyle.subHeadline} size={12}>
          <Typography variant={"h6"}>Zusammenfassung Ihres Anliegens</Typography>
        </Grid>
        <SummaryDetailsItem fieldPath={"finalClientDescriptionAndObjectives"} caseData={product} />

        {!isFlexServiceProduct(product) && (
          <>
            <Box id={"insuranceData"} sx={summaryStyle.scrollToAnchor} />
            <InsuranceData title={"Versicherung"} product={product} />
          </>
        )}
      </Grid>
      <form onSubmit={handleSubmit}>
        <FormSubmit
          sx={{ marginTop: "1rem" }}
          {...submitProps}
          hideBackButton={false}
          backLink={getCaseLink(product) + "/antrag/anliegen"}
          backText={"Alles bearbeiten"}
          nextText={"Angaben speichern"}
        />
      </form>
      <EmailVerificationModal />
    </ProductBox>
  );
};

export default Summary;
